import { useState, useEffect, useCallback, useContext } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Save, Shield, ShieldOff, RefreshCw,
  UserCheck, Mail, User as UserIcon, Settings, AlertCircle, Activity,
  AppWindow, ExternalLink, Globe, Lock, Users
} from 'lucide-react'
import {
  getUserDetail, impersonateUser, updateApp, updateUser, getUserActivityLog,
  listAllApps,
  type UserDetail as UserDetailType, type AuditLogEvent, type AdminApp
} from '../../lib/api'
import { UserContext } from '../../hooks/useUser'
import ActivityTimeline from '../../components/ActivityTimeline'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Switch } from '../../components/ui/Switch'
function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleString()
}

interface RateLimitConfig {
  disabled: boolean
  multiplier: number
}

const DEFAULT_RATE_LIMIT_CONFIG: RateLimitConfig = {
  disabled: false,
  multiplier: 1,
}

function toObjectRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {}
}

function parseRateLimitConfig(settings: Record<string, unknown>): RateLimitConfig {
  const rateLimits = toObjectRecord(settings.rate_limits)
  const multiplierValue = rateLimits.multiplier
  const parsedMultiplier = typeof multiplierValue === 'number'
    ? multiplierValue
    : typeof multiplierValue === 'string'
      ? Number.parseFloat(multiplierValue)
      : 1

  return {
    disabled: rateLimits.disabled === true,
    multiplier: Number.isFinite(parsedMultiplier) && parsedMultiplier > 0 ? parsedMultiplier : 1,
  }
}

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const currentUser = useContext(UserContext)

  const [user, setUser] = useState<UserDetailType | null>(null)
  const [activityEvents, setActivityEvents] = useState<AuditLogEvent[]>([])
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const [activityLoading, setActivityLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [rateLimitDisabled, setRateLimitDisabled] = useState(false)
  const [rateLimitMultiplier, setRateLimitMultiplier] = useState('1')

  const loadUser = useCallback(async () => {
    if (!userId) return
    try {
      const [userData, appsData] = await Promise.all([
        getUserDetail(parseInt(userId)),
        listAllApps(parseInt(userId)).catch(() => []),
      ])
      setUser(userData)
      setApps(appsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }, [userId])

  const loadActivity = useCallback(async () => {
    if (!userId) return
    setActivityLoading(true)
    try {
      const data = await getUserActivityLog(parseInt(userId), { limit: 50 })
      setActivityEvents(data.events)
    } catch (err) {
      // Ignore errors
    } finally {
      setActivityLoading(false)
    }
  }, [userId])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  useEffect(() => {
    if (!user) return
    const config = parseRateLimitConfig(user.settings)
    setRateLimitDisabled(config.disabled)
    setRateLimitMultiplier(String(config.multiplier))
  }, [user])

  async function handleImpersonate() {
    if (!userId) return
    setActionLoading('impersonate')
    setError(null)

    try {
      await impersonateUser(parseInt(userId))
      // impersonateUser redirects
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to impersonate')
      setActionLoading(null)
    }
  }

  async function handleToggleAdmin() {
    if (!userId || !user) return
    setActionLoading('toggle-admin')
    try {
      await updateUser(parseInt(userId), { is_admin: !user.is_admin })
      await loadUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleToggleActive() {
    if (!userId || !user) return
    setActionLoading('toggle-active')
    try {
      await updateUser(parseInt(userId), { is_active: !user.is_active })
      await loadUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setActionLoading(null)
    }
  }

  function handleResetRateLimitForm() {
    if (!user) return
    const config = parseRateLimitConfig(user.settings)
    setRateLimitDisabled(config.disabled)
    setRateLimitMultiplier(String(config.multiplier))
    setError(null)
  }

  async function handleSaveRateLimits() {
    if (!userId || !user) return

    const parsedMultiplier = Number.parseFloat(rateLimitMultiplier.trim() || '1')
    if (!rateLimitDisabled && (!Number.isFinite(parsedMultiplier) || parsedMultiplier <= 0)) {
      setError('Rate limit multiplier must be a positive number')
      return
    }

    setActionLoading('save-rate-limits')
    setError(null)
    setSuccess(null)

    try {
      const nextSettings: Record<string, unknown> = { ...user.settings }
      const nextRateLimits: Record<string, unknown> = {}

      if (rateLimitDisabled) {
        nextRateLimits.disabled = true
      }
      if (!rateLimitDisabled && Math.abs(parsedMultiplier - 1) > 0.000001) {
        nextRateLimits.multiplier = parsedMultiplier
      }

      if (Object.keys(nextRateLimits).length === 0) {
        delete nextSettings.rate_limits
      } else {
        nextSettings.rate_limits = nextRateLimits
      }

      await updateUser(parseInt(userId), { settings: nextSettings })
      await loadUser()
      setSuccess('Rate limit overrides saved')
      window.setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rate limit overrides')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleToggleAppRateLimit(app: AdminApp) {
    setActionLoading(`toggle-app-rate-limit:${app.name}`)
    setError(null)
    setSuccess(null)

    try {
      await updateApp(app.name, { rate_limit_exempt: !app.rate_limit_exempt })
      await loadUser()
      setSuccess(`Global rate-limit bypass ${app.rate_limit_exempt ? 'disabled' : 'enabled'} for ${app.display_name || app.name}`)
      window.setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update app rate-limit bypass')
    } finally {
      setActionLoading(null)
    }
  }

  const initialRateLimitConfig = user ? parseRateLimitConfig(user.settings) : DEFAULT_RATE_LIMIT_CONFIG
  const rateLimitFormChanged = user
    && (
      rateLimitDisabled !== initialRateLimitConfig.disabled
      || rateLimitMultiplier.trim() !== String(initialRateLimitConfig.multiplier)
    )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">User not found</h2>
        <Link to="/admin/users" className="text-primary hover:underline">
          Back to Users
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {user.settings?.first_name && user.settings?.last_name
              ? `${String(user.settings.first_name)} ${String(user.settings.last_name)}`
              : String(user.settings?.first_name || user.name || user.email.split('@')[0])}
          </h1>
          <p className="text-muted-foreground">{user.email}</p>
        </div>
        {currentUser?.email !== user.email && (
          <ConfirmDialog
            title="Impersonate User"
            description={`Start impersonating ${String(user.settings?.first_name || user.name || user.email)}? You will be logged in as them.`}
            confirmLabel="Impersonate"
            onConfirm={handleImpersonate}
            trigger={
              <button
                disabled={actionLoading === 'impersonate'}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                <UserCheck className="w-4 h-4" />
                Impersonate
              </button>
            }
          />
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            User Information
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {user.email}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">User ID</span>
              <span className="font-mono text-sm">{user.id}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <button
                onClick={handleToggleActive}
                disabled={actionLoading === 'toggle-active'}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  user.is_active
                    ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {user.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Role</span>
              <button
                onClick={handleToggleAdmin}
                disabled={actionLoading === 'toggle-admin'}
                className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition-colors ${
                  user.is_admin
                    ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {user.is_admin ? (
                  <>
                    <Shield className="w-3 h-3" /> Admin
                  </>
                ) : (
                  <>
                    <ShieldOff className="w-3 h-3" /> User
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Created</span>
              <span className="text-sm">{formatDate(user.created_at)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last Login</span>
              <span className="text-sm">{formatDate(user.last_login)}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium">Rate Limits</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Scale this user&apos;s limits or bypass them entirely. App-wide bypasses are configured per app below.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Disable all</span>
                <Switch
                  checked={rateLimitDisabled}
                  onCheckedChange={setRateLimitDisabled}
                  disabled={actionLoading === 'save-rate-limits'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">Limit Multiplier</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={rateLimitMultiplier}
                onChange={(e) => setRateLimitMultiplier(e.target.value)}
                disabled={rateLimitDisabled || actionLoading === 'save-rate-limits'}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
              <p className="text-xs text-muted-foreground">
                {rateLimitDisabled
                  ? 'All route limits are bypassed while this toggle is enabled.'
                  : 'Use 1 for platform defaults, 2 to double limits, or 0.5 to tighten them.'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveRateLimits}
                disabled={actionLoading === 'save-rate-limits' || !rateLimitFormChanged}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {actionLoading === 'save-rate-limits' ? 'Saving...' : 'Save Rate Limits'}
              </button>
              <button
                onClick={handleResetRateLimitForm}
                disabled={actionLoading === 'save-rate-limits' || !rateLimitFormChanged}
                className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Settings JSON */}
          {Object.keys(user.settings).length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </h3>
              <pre className="text-xs bg-secondary p-3 rounded-lg overflow-auto max-h-40">
                {JSON.stringify(user.settings, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Apps Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AppWindow className="w-5 h-5" />
            Apps ({apps.length})
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            App rate-limit bypass is global for that app, regardless of which user opens it.
          </p>

          {apps.length === 0 ? (
            <p className="text-muted-foreground text-sm">No apps created</p>
          ) : (
            <div className="space-y-3">
              {apps.map((app) => {
                const statusColor = app.status === 'running' ? 'bg-green-500' :
                  app.status === 'starting' ? 'bg-amber-500' :
                  app.status === 'error' ? 'bg-red-500' : 'bg-gray-500'
                const AccessIcon = app.access_level === 'public' ? Globe :
                  app.access_level === 'platform' ? Users :
                  app.access_level === 'specific' ? UserCheck : Lock

                return (
                  <Link
                    key={app.id}
                    to={`/admin/apps/${encodeURIComponent(app.name)}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className={`w-2 h-2 rounded-full ${statusColor}`} />
                        {app.status === 'running' && (
                          <div className={`absolute inset-0 w-2 h-2 rounded-full ${statusColor} animate-ping`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{app.display_name || app.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">/a/{app.name}/</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleToggleAppRateLimit(app)
                        }}
                        disabled={actionLoading === `toggle-app-rate-limit:${app.name}`}
                        className={`px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 ${
                          app.rate_limit_exempt
                            ? 'bg-amber-500/20 text-amber-600 hover:bg-amber-500/30'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        title={app.rate_limit_exempt ? 'Disable global rate-limit bypass' : 'Enable global rate-limit bypass'}
                      >
                        {app.rate_limit_exempt ? 'No Limit' : 'Rate Limited'}
                      </button>
                      <AccessIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded hover:bg-secondary"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>


        {/* User Activity Card */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              User Activity
            </h2>
            
              <button title="Reload the user's recent activity"
                onClick={loadActivity}
                disabled={activityLoading}
                className="p-2 rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
              </button>
            
          </div>
          <ActivityTimeline events={activityEvents} loading={activityLoading} />
        </div>

      </div>
    </div>
  )
}
